import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';

import InputAdornment from '~/components/core/Atomic/InputAdornment';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { shouldAutoPaginate } from '~/components/core/Tables/SortableTable/utils';
import Text from '~/components/core/TextComponents/Text';

import PlainTable from './PlainTable';

type Row = Record<string, unknown>;
type Column = Record<string, unknown>;

export function rowMatchesQuery(row: Row, query: string, columns: Column[]): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return true;
  }
  // Rows can be undefined (e.g. covered_vehicles?.map((cv) => cv?.vehicle) yields gaps); such a row
  // has no searchable cell, so reading row[column.id] below would throw. Treat it as a non-match.
  if (row === null || row === undefined) {
    return false;
  }

  const isMatch = (value: unknown): boolean => {
    // null/undefined render as blank cells, so they are not searchable on their own. A column
    // that wants null to read as the text "null" does so via a specialCell (JSON.stringify),
    // and that branch hands isMatch the literal "null" string below.
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value).toLowerCase().includes(trimmed);
    }
    if (Array.isArray(value)) {
      return value.some(isMatch);
    }
    if (typeof value === 'object') {
      // Ignore React elements (circular, would throw on JSON.stringify).
      if ('$$typeof' in value) {
        return false;
      }
      // Plain objects: traverse values only; matching against key names is misleading.
      const proto = Object.getPrototypeOf(value);
      if (proto === null || proto === Object.prototype) {
        return Object.values(value as Record<string, unknown>).some(isMatch);
      }
      // Non-plain objects (e.g. Date) render via JSON.stringify, so match that same text.
      try {
        return (JSON.stringify(value) || '').toLowerCase().includes(trimmed);
      } catch {
        return false;
      }
    }
    return false;
  };

  // Match only against the cells actually rendered for visible columns. Rows may carry extra
  // properties (e.g. PolicySearch merges every additional_fields key onto the row while only a
  // few become columns); searching those would keep rows visible on text the user cannot see.
  return columns
    .filter((column) => !column.isHidden)
    .some((column) => {
      const rendered =
        typeof column.specialCell === 'function'
          ? (column.specialCell as (row: Row) => unknown)(row)
          : row[column.id as string];
      return isMatch(rendered);
    });
}

interface SearchableSubTableProps {
  columns: Column[];
  rows?: Row[];
  searchPlaceholder?: string;
  searchLabel?: string;
  rowsPerPage?: number;
}

const SearchableSubTable: React.FC<SearchableSubTableProps> = ({
  columns,
  rows = [],
  searchPlaceholder = 'Search',
  searchLabel,
  rowsPerPage = 10,
}) => {
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState('');

  const debouncedSetQuery = useMemo(() => debounce((value: string) => setQuery(value), 300), []);

  useEffect(() => {
    // Clearing the field must take effect immediately: otherwise the debounced query lingers on the
    // old term for up to 300ms, leaving rows filtered and "No matching results" showing over an empty
    // search box. Only non-empty input is debounced.
    if (searchText === '') {
      debouncedSetQuery.cancel();
      setQuery('');
      return undefined;
    }
    debouncedSetQuery(searchText);
    return () => debouncedSetQuery.cancel();
  }, [searchText, debouncedSetQuery]);

  const filteredRows = useMemo(
    () => rows.filter((row) => rowMatchesQuery(row, query, columns)),
    [rows, query, columns]
  );

  const hasActiveQuery = query.trim().length > 0;

  // Only surface the search box once the list actually paginates: below that it sits above a single
  // page of rows with nothing to filter. `shouldAutoPaginate` is SortableTable's own rule, imported
  // rather than re-derived so the two cannot drift apart. Gate on the TOTAL row count, not the
  // filtered count, so the box does not disappear while a query narrows the visible rows below one
  // page; and keep it visible whenever a search is active so a shrinking rows prop cannot strand the
  // user with an un-clearable query.
  const showSearch = shouldAutoPaginate(rows.length, rowsPerPage) || searchText !== '';

  return (
    <div>
      {showSearch && (
        <div className="pb-12">
          <TextField
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(value: string) => setSearchText(value)}
            size="small"
            // Keep the search box compact rather than spanning the full sub-table width.
            className="w-[280px]"
            InputProps={{
              // The standard underline darkens on hover, which reads as an unexpected colour change
              // on a plain search box; drop it and let the search icon carry the affordance.
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
      )}
      <PlainTable
        // SortableTable keeps its pagination page in internal state and only resets it on sort, so
        // a search that shrinks the list can leave it on a page past the end (empty body + a false
        // "No matching results"). Re-key on the active query to remount and reset to the first page.
        key={query}
        columns={columns}
        rows={filteredRows}
        autoPaginateRowsPerPage={rowsPerPage}
        emptyStateComponent={
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {hasActiveQuery ? 'No matching results' : 'No results'}
          </Text>
        }
      />
    </div>
  );
};

export default SearchableSubTable;
